<template>
  <el-container class="main-layout">
    <!-- 侧边栏 -->
    <el-aside :width="isCollapse ? '64px' : '200px'" class="main-layout__aside">
      <div class="logo-container" @click="toggleCollapse">
        <img v-if="!isCollapse" src="@/assets/logo.png" alt="SplatChat" class="logo" />
        <img v-else src="@/assets/logo.png" alt="SC" class="logo-mini" />
      </div>

      <el-menu
        :default-active="activeMenu"
        :collapse="isCollapse"
        :collapse-transition="false"
        router
        class="main-menu"
      >
        <template v-for="menu in menuList" :key="menu.path">
          <!-- 有子菜单的情况 -->
          <el-sub-menu v-if="menu.children && menu.children.length" :index="menu.path">
            <template #title>
              <el-icon>
                <component :is="menu.icon" />
              </el-icon>
              <span>{{ menu.title }}</span>
            </template>
            <el-menu-item v-for="child in menu.children" :key="child.path" :index="child.path">
              {{ child.title }}
            </el-menu-item>
          </el-sub-menu>

          <!-- 无子菜单的情况 -->
          <el-menu-item v-else :index="menu.path">
            <el-icon>
              <component :is="menu.icon" />
            </el-icon>
            <template #title>{{ menu.title }}</template>
          </el-menu-item>
        </template>
      </el-menu>

      <!-- 收缩按钮 -->
      <div class="collapse-btn" @click="toggleCollapse">
        <el-icon>
          <component :is="isCollapse ? 'Expand' : 'Fold'" />
        </el-icon>
      </div>
    </el-aside>

    <el-container class="main-layout__container">
      <!-- 顶部栏 -->
      <el-header class="main-layout__header">
        <div class="header-left">
          <!-- 面包屑导航 -->
          <el-breadcrumb separator="/">
            <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
            <el-breadcrumb-item v-for="item in breadcrumbs" :key="item.path">
              {{ item.title }}
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>

        <div class="header-right">
          <!-- 时区选择器 -->
          <TimezoneSelector />

          <!-- 员工信息 -->
          <el-dropdown>
            <div class="staff-info">
              <el-avatar :size="32">
                {{ staffData?.info?.username?.charAt(0)?.toUpperCase() || 'S' }}
              </el-avatar>
              <span class="staff-name">{{ staffData?.info?.username || 'Staff' }}</span>
              <el-icon><ArrowDown /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="handleProfile">
                  <el-icon><User /></el-icon>
                  个人资料
                </el-dropdown-item>
                <el-dropdown-item @click="handleChangePassword">
                  <el-icon><Key /></el-icon>
                  修改密码
                </el-dropdown-item>
                <el-dropdown-item divided @click="handleLogout">
                  <el-icon><SwitchButton /></el-icon>
                  退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>

      <!-- 标签页栏 -->
      <TabBar />

      <!-- 主内容区 -->
      <el-main class="main-layout__main">
        <router-view v-slot="{ Component, route }">
          <transition name="fade" mode="out-in">
            <keep-alive :include="cacheList">
              <component :is="Component" :key="route.fullPath" />
            </keep-alive>
          </transition>
        </router-view>
      </el-main>
    </el-container>
  </el-container>
</template>

<script setup lang="ts" name="MainLayout">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStaffStore } from '@/stores/staff'
import { useTabsStore } from '@/stores/tabs'
import { useMenu } from '@/composables/useMenu'
import TimezoneSelector from '@/components/common/TimezoneSelector.vue'
import TabBar from './components/TabBar.vue'
import { ArrowDown, Key, SwitchButton, User } from '@element-plus/icons-vue'
import { ElMessageBox } from 'element-plus'

const route = useRoute()
const router = useRouter()
const staffStore = useStaffStore()
const tabsStore = useTabsStore()

// 使用菜单组合式函数
const { menuList, cachedPages, getBreadcrumbs } = useMenu()

onMounted(() => {
  console.log(menuList.value)
  // 恢复标签页
  tabsStore.restoreTabs()
  // 添加当前路由为标签页
  if (route.name) {
    tabsStore.addTab(route)
  }
})

// 状态
const isCollapse = ref(false)

// 计算属性
const activeMenu = computed(() => route.path)
const staffData = computed(() => staffStore.staffData)
const cacheList = computed(() => tabsStore.getCachedComponents())

// 监听路由变化，自动添加标签页
watch(
  () => route.fullPath,
  () => {
    if (route.name) {
      tabsStore.addTab(route)
    }
  }
)

// 面包屑
const breadcrumbs = ref<Array<{ path: string; title: string }>>([])

// 更新面包屑的函数
const updateBreadcrumbs = (path: string) => {
  breadcrumbs.value = getBreadcrumbs(path)
}

// 监听路由变化，更新面包屑
watch(
  () => route.path,
  (path) => {
    updateBreadcrumbs(path)
  },
  { immediate: true }
)

// 切换侧边栏展开/收缩
const toggleCollapse = () => {
  isCollapse.value = !isCollapse.value
}

// 个人资料
const handleProfile = () => {
  router.push('/profile')
}

// 修改密码 - 跳转到个人资料页面
const handleChangePassword = () => {
  router.push('/profile')
}

// 退出登录
const handleLogout = async () => {
  try {
    await ElMessageBox.confirm('确定要退出登录吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    })

    await staffStore.logout()
    tabsStore.clearTabs() // 清空所有标签页
    router.push('/login')
  } catch {
    // 用户取消
  }
}
</script>

<style lang="scss" scoped>
.main-layout {
  height: 100vh;

  &__aside {
    background-color: #304156;
    transition: width 0.3s;
    position: relative;
    display: flex;
    flex-direction: column;

    .logo-container {
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #2b3749;
      flex-shrink: 0;

      .logo {
        height: 30px;
      }

      .logo-mini {
        height: 24px;
      }
    }

    .main-menu {
      flex: 1;
      overflow-y: auto;
      border-right: none;
      background-color: #304156;

      :deep(.el-menu-item),
      :deep(.el-sub-menu__title) {
        color: #bfcbd9;
        background-color: #304156;
        &:hover {
          background-color: #263445 !important;
        }

        &.is-active {
          color: #409eff;
          background-color: #1f2d3d !important;
        }
      }

      :deep(.el-menu-item) {
        &.is-active {
          color: #409eff;
          background-color: #1f2d3d !important;
        }
      }
    }

    .collapse-btn {
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      cursor: pointer;
      color: #bfcbd9;
      background-color: #2b3749;
      transition: background-color 0.3s;

      &:hover {
        background-color: #263445;
      }
    }
  }

  &__container {
    display: flex;
    flex-direction: column;
    background-color: #f5f7fa;
  }

  &__header {
    height: 48px;
    background-color: #fff;
    box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;

    .header-left {
      display: flex;
      align-items: center;
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 20px;

      .staff-info {
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;

        .staff-name {
          color: #606266;
          font-size: 14px;
        }
      }
    }
  }

  &__main {
    padding: 4px 16px;
    overflow-y: auto;
  }
}

// 过渡动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
